import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../Helper/api";
import { formatDate } from "../i18n";
import { MAX_TRAVEL_DAYS, formatAmount } from "../Helper/travelAllowance";

const popularCountries = [
  "Almanya",
  "Amerika Birleşik Devletleri",
  "Avusturya",
  "Azerbaycan",
  "Belçika",
  "Birleşik Arap Emirlikleri",
  "Birleşik Krallık",
  "Çin",
  "Fransa",
  "Hollanda",
  "İngiltere",
  "İspanya",
  "İsveç",
  "İsviçre",
  "İtalya",
  "Japonya",
  "Katar",
  "Polonya",
  "Rusya",
  "Suudi Arabistan",
  "Ukrayna"
];

const CURRENCIES = ["USD", "EUR", "GBP", "TRY"];
const LOCAL_CURRENCY = "TRY";
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const calculateTlEquivalent = (dailyAllowance, exchangeRate) =>
  parseFloat(((parseFloat(dailyAllowance) || 0) * (parseFloat(exchangeRate) || 0)).toFixed(4));

// Kur, TCMB'den gelene (ya da elle girilene) kadar boş kalır; böylece eksik kurla form gönderilemez
const createEmptyRow = (date) => ({
  date,
  country: "",
  city: "",
  accommodation: "",
  purpose: "",
  dailyAllowance: "",
  currency: "USD",
  exchangeRate: "",
  tlEquivalent: 0,
});

// YYYY-MM-DD biçimindeki iki tarih arasındaki günler (uçlar dahil)
const getDatesBetween = (startDate, endDate) => {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const dates = [];
  for (let time = start; time <= end; time += DAY_IN_MS) {
    dates.push(new Date(time).toISOString().slice(0, 10));
  }
  return dates;
};

// Get yesterday's date formatted as YYYY-MM-DD in local time
const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fieldClass =
  "border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 transition";
const cellInputClass =
  "w-full border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500";

function CreateTravelAllowancePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth.loginUser);

  const [project, setProject] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCountryDropdownIdx, setActiveCountryDropdownIdx] = useState(null);

  const [users, setUsers] = useState([]);
  const [selectedTc, setSelectedTc] = useState(auth?.tc || "");

  useEffect(() => {
    api.get("/auth/users")
      .then((res) => setUsers(res.data.users || []))
      .catch((err) => {
        console.error("Kullanıcılar yüklenemedi:", err);
        toast.error(t("travelAllowance.usersLoadError"));
      });
  }, [t]);

  const travelDates = startDate && endDate && endDate >= startDate ? getDatesBetween(startDate, endDate) : [];
  const exceedsMaxDays = travelDates.length > MAX_TRAVEL_DAYS;

  // Fetch the TCMB rate of a row; the row is matched by date + currency so a late response
  // never overwrites a row the user has changed in the meantime
  const fetchRate = async (date, currency) => {
    const res = await api.get("/travel-allowances/rates", { params: { date, currency } });
    const rate = res.data?.rate;
    if (typeof rate !== "number") {
      throw new Error("Invalid rate response");
    }
    setItems((prev) =>
      prev.map((row) =>
        row.date === date && row.currency === currency
          ? { ...row, exchangeRate: rate, tlEquivalent: calculateTlEquivalent(row.dailyAllowance, rate) }
          : row
      )
    );
  };

  // Generate daily rows when the date range changes; rows of days that stay in range are kept
  useEffect(() => {
    const dates = startDate && endDate && endDate >= startDate ? getDatesBetween(startDate, endDate) : [];
    if (dates.length === 0 || dates.length > MAX_TRAVEL_DAYS) {
      setItems([]);
      return;
    }

    const existingByDate = new Map(items.map((item) => [item.date, item]));
    setItems(dates.map((date) => existingByDate.get(date) || createEmptyRow(date)));

    const newDates = dates.filter((date) => !existingByDate.has(date));
    const defaultCurrency = createEmptyRow("").currency;
    Promise.allSettled(newDates.map((date) => fetchRate(date, defaultCurrency))).then((results) => {
      if (results.some((result) => result.status === "rejected")) {
        toast.warning(t("travelAllowance.ratesFetchFailedBulk"));
      }
    });
    // Yalnızca tarih aralığı değiştiğinde çalışmalı; satır düzenlemeleri bu etkiyi tetiklememeli
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleRowChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, [field]: value };
        if (field === "currency") {
          // Önceki para biriminin kuru yeni para birimine taşınmaz
          next.exchangeRate = value === LOCAL_CURRENCY ? 1 : "";
        }
        next.tlEquivalent = calculateTlEquivalent(next.dailyAllowance, next.exchangeRate);
        return next;
      })
    );

    if (field === "currency" && value !== LOCAL_CURRENCY) {
      const { date } = items[index];
      fetchRate(date, value).catch(() => {
        toast.warning(t("travelAllowance.rateFetchFailed", { date: formatDate(date), currency: value }));
      });
    }
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.tlEquivalent || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project.trim()) {
      toast.error(t("travelAllowance.projectRequired"));
      return;
    }
    if (!startDate || !endDate) {
      toast.error(t("travelAllowance.datesRequired"));
      return;
    }

    const yesterdayStr = getYesterdayStr();
    if (startDate > yesterdayStr || endDate > yesterdayStr) {
      toast.error(t("travelAllowance.pastDatesOnly"));
      return;
    }

    if (startDate > endDate) {
      toast.error(t("travelAllowance.endBeforeStart"));
      return;
    }
    if (exceedsMaxDays) {
      toast.error(t("travelAllowance.maxDays", { max: MAX_TRAVEL_DAYS }));
      return;
    }
    if (items.length === 0) {
      toast.error(t("travelAllowance.noDays"));
      return;
    }

    // Validate that required fields are filled for each day (except accommodation)
    const invalidIndex = items.findIndex((item) => {
      const hasText = (value) => String(value ?? "").trim() !== "";
      const dailyAllowance = Number(item.dailyAllowance);
      const exchangeRate = Number(item.exchangeRate);
      return (
        !hasText(item.country) ||
        !hasText(item.city) ||
        !hasText(item.purpose) ||
        !hasText(item.currency) ||
        !hasText(item.dailyAllowance) ||
        !Number.isFinite(dailyAllowance) ||
        dailyAllowance < 0 ||
        !Number.isFinite(exchangeRate) ||
        exchangeRate <= 0
      );
    });
    if (invalidIndex !== -1) {
      toast.error(t("travelAllowance.rowIncomplete", { day: invalidIndex + 1 }));
      return;
    }

    setLoading(true);
    try {
      await api.post("/travel-allowances", {
        tc: selectedTc,
        project,
        startDate,
        endDate,
        note,
        items,
      });
      toast.success(t("travelAllowance.createSuccess"));
      navigate("/harcirahlar");
    } catch (error) {
      toast.error(error.response?.data?.message || t("travelAllowance.createError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-slate-800">{t("travelAllowance.createTitle")}</h1>
        <button
          onClick={() => navigate("/harcirahlar")}
          className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 transition"
        >
          {t("travelAllowance.back")}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Seyahat Bilgileri */}
        <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="travel-allowance-employee" className="text-sm font-semibold text-slate-600">
              {t("travelAllowance.employee")}
            </label>
            <select
              id="travel-allowance-employee"
              name="employee"
              value={selectedTc}
              onChange={(e) => setSelectedTc(e.target.value)}
              className={`${fieldClass} bg-white`}
              required
            >
              <option value="">{t("travelAllowance.selectEmployee")}</option>
              {users.map((u) => (
                <option key={u.tc} value={u.tc}>
                  {u.name} {u.surname} ({u.tc})
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="travel-allowance-project" className="text-sm font-semibold text-slate-600">
              {t("travelAllowance.project")}
            </label>
            <input
              type="text"
              id="travel-allowance-project"
              name="project"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder={t("travelAllowance.projectPlaceholder")}
              className={fieldClass}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="travel-allowance-start" className="text-sm font-semibold text-slate-600">
              {t("travelAllowance.travelStart")}
            </label>
            <input
              type="date"
              id="travel-allowance-start"
              name="startDate"
              value={startDate}
              max={getYesterdayStr()}
              onChange={(e) => setStartDate(e.target.value)}
              className={fieldClass}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="travel-allowance-end" className="text-sm font-semibold text-slate-600">
              {t("travelAllowance.travelEnd")}
            </label>
            <input
              type="date"
              id="travel-allowance-end"
              name="endDate"
              value={endDate}
              min={startDate || undefined}
              max={getYesterdayStr()}
              onChange={(e) => setEndDate(e.target.value)}
              className={fieldClass}
              required
            />
          </div>
        </div>

        {exceedsMaxDays && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {t("travelAllowance.maxDays", { max: MAX_TRAVEL_DAYS })}
          </div>
        )}

        {/* Günlük Harcırah Ayrıntıları */}
        {items.length > 0 && (
          <div className="bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-700">
                {t("travelAllowance.dailyLogs", { count: items.length })}
              </h2>
              <span className="text-sm text-slate-500">{t("travelAllowance.ratesHint")}</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100/50 text-slate-600 text-xs font-semibold uppercase border-b border-slate-100">
                    <th className="px-4 py-3 w-10">{t("travelAllowance.no")}</th>
                    <th className="px-4 py-3 min-w-[120px]">{t("travelAllowance.date")}</th>
                    <th className="px-4 py-3 min-w-[130px]">{t("travelAllowance.country")}</th>
                    <th className="px-4 py-3 min-w-[130px]">{t("travelAllowance.city")}</th>
                    <th className="px-4 py-3 min-w-[150px]">{t("travelAllowance.accommodation")}</th>
                    <th className="px-4 py-3 min-w-[160px]">{t("travelAllowance.purpose")}</th>
                    <th className="px-4 py-3 min-w-[140px]">{t("travelAllowance.amount")}</th>
                    <th className="px-4 py-3 min-w-[120px]">{t("travelAllowance.currency")}</th>
                    <th className="px-4 py-3 min-w-[150px]">{t("travelAllowance.rate")}</th>
                    <th className="px-4 py-3 min-w-[160px] text-right">{t("travelAllowance.tlEquivalent")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {items.map((item, idx) => (
                    <tr key={item.date} className="hover:bg-slate-50/50 transition">
                      <td className="px-4 py-3 text-slate-400 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <input
                          type="date"
                          name={`items[${idx}].date`}
                          aria-label={t("travelAllowance.date")}
                          value={item.date}
                          className={`${cellInputClass} bg-slate-50`}
                          readOnly
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative">
                          <input
                            type="text"
                            name={`items[${idx}].country`}
                            aria-label={t("travelAllowance.country")}
                            autoComplete="off"
                            value={item.country}
                            onChange={(e) => {
                              handleRowChange(idx, "country", e.target.value);
                              setActiveCountryDropdownIdx(idx);
                            }}
                            onFocus={() => setActiveCountryDropdownIdx(idx)}
                            onBlur={() => setTimeout(() => setActiveCountryDropdownIdx(null), 200)}
                            placeholder={t("travelAllowance.country")}
                            className={cellInputClass}
                            required
                          />
                          {activeCountryDropdownIdx === idx && (
                            <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-100 rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto py-1 text-left">
                              {popularCountries
                                .filter((c) =>
                                  c.toLocaleLowerCase("tr").includes((item.country || "").toLocaleLowerCase("tr"))
                                )
                                .map((c) => (
                                  <button
                                    key={c}
                                    type="button"
                                    onMouseDown={() => {
                                      handleRowChange(idx, "country", c);
                                      setActiveCountryDropdownIdx(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
                                  >
                                    {c}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          name={`items[${idx}].city`}
                          aria-label={t("travelAllowance.city")}
                          value={item.city}
                          onChange={(e) => handleRowChange(idx, "city", e.target.value)}
                          placeholder={t("travelAllowance.city")}
                          className={cellInputClass}
                          required
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          name={`items[${idx}].accommodation`}
                          aria-label={t("travelAllowance.accommodation")}
                          value={item.accommodation}
                          onChange={(e) => handleRowChange(idx, "accommodation", e.target.value)}
                          placeholder={t("travelAllowance.accommodationPlaceholder")}
                          className={cellInputClass}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          name={`items[${idx}].purpose`}
                          aria-label={t("travelAllowance.purpose")}
                          value={item.purpose}
                          onChange={(e) => handleRowChange(idx, "purpose", e.target.value)}
                          placeholder={t("travelAllowance.purpose")}
                          className={cellInputClass}
                          required
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          name={`items[${idx}].dailyAllowance`}
                          aria-label={t("travelAllowance.amount")}
                          value={item.dailyAllowance}
                          onChange={(e) => handleRowChange(idx, "dailyAllowance", e.target.value)}
                          placeholder={t("travelAllowance.amount")}
                          className={`${cellInputClass} text-right`}
                          required
                        />
                      </td>
                      <td className="px-4 py-3">
                        <select
                          name={`items[${idx}].currency`}
                          aria-label={t("travelAllowance.currency")}
                          value={item.currency}
                          onChange={(e) => handleRowChange(idx, "currency", e.target.value)}
                          className={cellInputClass}
                        >
                          {CURRENCIES.map((currency) => (
                            <option key={currency} value={currency}>
                              {currency}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.0001"
                          min="0.0001"
                          name={`items[${idx}].exchangeRate`}
                          aria-label={t("travelAllowance.rate")}
                          value={item.exchangeRate}
                          onChange={(e) => handleRowChange(idx, "exchangeRate", e.target.value)}
                          className={`${cellInputClass} text-right`}
                          required
                        />
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-700">
                        {formatAmount(item.tlEquivalent || 0)} ₺
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Açıklama / Not & Toplam Bilgisi */}
        <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="flex flex-col gap-1.5 flex-1">
            <label htmlFor="travel-allowance-note" className="text-sm font-semibold text-slate-600">
              {t("travelAllowance.note")}
            </label>
            <textarea
              rows="3"
              id="travel-allowance-note"
              name="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("travelAllowance.notePlaceholder")}
              className={`${fieldClass} resize-none w-full`}
            />
          </div>

          <div className="bg-slate-50 p-6 rounded-lg border border-slate-100 flex flex-col gap-1 items-end min-w-[240px]">
            <span className="text-xs font-semibold text-slate-500 uppercase">{t("travelAllowance.totalAmount")}</span>
            <span className="text-3xl font-black text-blue-600">{formatAmount(totalAmount)} ₺</span>
          </div>
        </div>

        {/* Gönder Butonu */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/harcirahlar")}
            className="px-6 py-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
          >
            {t("travelAllowance.cancel")}
          </button>
          <button
            type="submit"
            disabled={loading || items.length === 0}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-bold disabled:bg-blue-300 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20"
          >
            {loading ? t("travelAllowance.submitting") : t("travelAllowance.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateTravelAllowancePage;
